import { CheckCircle2, AlertCircle, Loader2, ChevronDown, ChevronUp, Save, Eye, EyeOff } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { RichTextEditor } from '../../components/ui/RichTextEditor';
import { useDefaultWaiverTemplates, useSaveDefaultWaiver } from '../../hooks/useDefaultWaiver';
import { WAIVER_PLACEHOLDERS } from '../../types/damageWaiver.types';

const DEFAULT_WAIVER_HTML = `<h1 style="text-align: center">{{gym_name}}</h1>
<p style="text-align: center">{{gym_address}}</p>
<p style="text-align: center">{{user_phone}} | {{user_email}}</p>

<hr>

<h2 style="text-align: center">LIABILITY WAIVER AND RELEASE OF CLAIMS</h2>
<p style="text-align: center"><em>Assumption of Risk, Waiver of Liability, and Indemnity Agreement</em></p>

<h3>1. ACKNOWLEDGMENT AND ASSUMPTION OF RISKS</h3>
<p>I, the undersigned participant, acknowledge that the use of fitness facilities, equipment, services, programs, and activities (collectively, "Activities") offered by the above-named facility (the "Facility") involves inherent risks of physical injury, illness, disability, or death. These risks may result from my own actions or inactions, the actions or inactions of others, the condition of the premises, or the nature of the Activities themselves.</p>
<p>I understand that such risks may include, but are not limited to: muscle strains, sprains, tears, and fractures; cardiovascular incidents including heart attack or stroke; injuries arising from the use of free weights, machines, cardiovascular equipment, resistance bands, or other fitness apparatus; injuries resulting from participation in group classes, personal training sessions, or open gym use; exposure to communicable diseases; slips, trips, and falls; and any other injury or harm that may occur in connection with the Facility or its Activities.</p>
<p>I voluntarily assume full responsibility for any and all risks of injury, illness, disability, or death that may arise from my participation in any Activities at the Facility, whether or not caused by the negligence of the Facility, its owners, officers, employees, agents, or independent contractors.</p>

<h3>2. RELEASE AND WAIVER OF LIABILITY</h3>
<p>In consideration of being permitted to use the Facility and participate in its Activities, I hereby release, waive, discharge, and covenant not to sue the Facility, its owners, operators, officers, directors, employees, agents, independent contractors, affiliates, successors, and assigns (collectively, the "Released Parties") from any and all liability, claims, demands, actions, or causes of action arising out of or related to any loss, damage, or injury, including death, that may be sustained by me, whether caused by the negligence of the Released Parties or otherwise, while participating in any Activities or while on the premises of the Facility.</p>

<h3>3. INDEMNIFICATION</h3>
<p>I agree to indemnify, defend, and hold harmless the Released Parties from any and all claims, actions, suits, procedures, costs, expenses, damages, and liabilities, including reasonable attorney's fees, brought by any third party as a result of my actions or omissions, my use of the Facility, or my participation in any Activities.</p>

<h3>4. MEDICAL ACKNOWLEDGMENT</h3>
<p>I represent and warrant that I am physically fit and have no medical condition that would prevent my full participation in Activities at the Facility. I acknowledge that it is my responsibility to consult with a physician prior to and regarding my participation in any Activities. I agree that the Facility is not responsible for any injury or health condition that may result from or be aggravated by my participation in Activities, including any pre-existing conditions.</p>
<p>I authorize the Facility and its staff to seek emergency medical treatment on my behalf in the event of an injury or medical emergency, and I agree to be financially responsible for any costs incurred as a result of such treatment.</p>

<h3>5. FACILITY RULES AND CONDUCT</h3>
<p>I agree to comply with all posted rules, regulations, and policies of the Facility, as well as any verbal instructions provided by Facility staff. I understand that failure to comply may result in the termination of my membership or access to the Facility without refund. I agree to use all equipment in the manner for which it was designed and to ask for instruction if I am unfamiliar with any equipment or Activity.</p>

<h3>6. PERSONAL PROPERTY</h3>
<p>I understand that the Facility is not responsible for any personal property that is lost, stolen, or damaged while on the premises, including items stored in lockers or other designated areas.</p>

<h3>7. PHOTOGRAPHY AND VIDEO</h3>
<p>I grant the Facility permission to use any photographs, video recordings, or other images taken of me while on the premises for promotional, marketing, or educational purposes, unless I notify the Facility in writing that I do not consent to such use.</p>

<h3>8. GOVERNING LAW AND SEVERABILITY</h3>
<p>This Agreement shall be governed by and construed in accordance with the laws of the state in which the Facility is located. If any provision of this Agreement is held to be invalid or unenforceable, the remaining provisions shall continue in full force and effect. This Agreement constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior or contemporaneous understandings or agreements.</p>

<h3>9. BINDING EFFECT</h3>
<p>I acknowledge that I have read this Liability Waiver and Release of Claims in its entirety, that I fully understand its terms, and that I am signing it voluntarily and of my own free will. I understand that this Agreement is legally binding and that by signing it, I am giving up substantial legal rights, including the right to sue. This waiver shall remain in effect for the duration of my membership or use of the Facility.</p>

<hr>

<h3>PARTICIPANT INFORMATION</h3>
<p><strong>Printed Name:</strong> {{member_name}}</p>
<p><strong>Date of Birth:</strong> _______________</p>
<p><strong>Address:</strong> _______________</p>
<p><strong>Phone Number:</strong> {{user_phone}}</p>
<p><strong>Email Address:</strong> {{user_email}}</p>
<p><strong>Emergency Contact Name &amp; Phone Number:</strong> _______________</p>

<h3>SIGNATURES</h3>
<p><strong>Participant Signature:</strong> _______________</p>
<p><strong>Date:</strong> {{current_date}}</p>

<hr>

<h3>FOR PARTICIPANTS UNDER 18 YEARS OF AGE</h3>
<p>I am the parent or legal guardian of the minor named above. I have read and understand this Liability Waiver and Release of Claims. I consent to my child's participation in Activities at the Facility and agree to the terms set forth herein on behalf of myself and my child. I further agree to indemnify and hold harmless the Released Parties from any claims brought by or on behalf of the minor.</p>
<p><strong>Parent/Guardian Signature:</strong> _______________</p>
<p><strong>Date:</strong> {{current_date}}</p>
<p><strong>Parent/Guardian Printed Name:</strong> {{signer_name}}</p>

<hr>

<p><em>This document is a general template and does not constitute legal advice. Facility owners should consult with a licensed attorney in their jurisdiction to ensure compliance with applicable laws.</em></p>`;

const SAMPLE_DATA: Record<string, string> = {
  gym_name: 'Acme Brazilian Jiu-Jitsu',
  gym_address: '123 Main Street, Suite 100, Austin, TX 78701',
  member_name: 'John Smith',
  signer_name: 'John Smith',
  student_name: 'John Smith',
  current_date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
  user_email: 'john.smith@example.com',
  user_phone: '(555) 123-4567',
};

function resolvePlaceholders(html: string): string {
  return html.replace(/\{\{(\w+)\}\}/g, (match, key) => SAMPLE_DATA[key] || match);
}

export default function DefaultWaiverEditorPage() {
  const navigate = useNavigate();
  const { data: templates, isLoading, refetch } = useDefaultWaiverTemplates();
  const { create, update, getNextVersion, isLoading: isSaving } = useSaveDefaultWaiver();

  const [editorContent, setEditorContent] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [nextVersion, setNextVersion] = useState('1.0');
  const [showPreview, setShowPreview] = useState(false);
  const [showOlderVersions, setShowOlderVersions] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const activeTemplate = templates.find(t => t.is_active) || templates[0] || null;
  const olderTemplates = templates.filter(t => t.id !== activeTemplate?.id);

  // Load active template content on mount, or fall back to hardcoded default
  useEffect(() => {
    if (hasChanges) return;
    if (activeTemplate) {
      setEditorContent(activeTemplate.content_html);
      setEditingId(activeTemplate.id);
    } else if (!isLoading && templates.length === 0) {
      setEditorContent(DEFAULT_WAIVER_HTML);
    }
  }, [activeTemplate, hasChanges, isLoading, templates.length]);

  // Fetch next version
  useEffect(() => {
    getNextVersion().then(setNextVersion);
  }, [getNextVersion, templates]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleContentChange = useCallback((html: string) => {
    setEditorContent(html);
    setHasChanges(true);
  }, []);

  const handleSaveNewVersion = async () => {
    if (!editorContent.trim()) {
      setNotification({ type: 'error', message: 'Waiver content cannot be empty.' });
      return;
    }

    try {
      await create({ version: nextVersion, content_html: editorContent });
      setNotification({ type: 'success', message: `Default waiver v${nextVersion} saved successfully!` });
      setHasChanges(false);
      refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save waiver';
      setNotification({ type: 'error', message });
    }
  };

  const handleUpdateCurrent = async () => {
    if (!editingId || !editorContent.trim()) return;

    try {
      await update(editingId, { content_html: editorContent });
      setNotification({ type: 'success', message: 'Waiver content updated successfully!' });
      setHasChanges(false);
      refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update waiver';
      setNotification({ type: 'error', message });
    }
  };

  const handleLoadVersion = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setEditorContent(template.content_html);
      setEditingId(template.id);
      setHasChanges(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#9B1421]" />
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Notification Toast */}
        {notification && (
          <div
            className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg ${
              notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}
          >
            {notification.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="font-medium">{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-70 transition-opacity">
              &times;
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="outline"
            className="mb-4 border-[#504A4A] text-[#A9A3A3] hover:bg-[#363232]"
          >
            &larr; Back to Admin Dashboard
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#EBFFEE] mb-2">Default Waiver Template</h1>
          <p className="text-sm sm:text-base text-[#A9A3A3]">
            Edit the default liability waiver that gyms can use. Use placeholders like <code className="text-rollops-accent">{'{{gym_name}}'}</code> for dynamic content.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardContent className="p-0">
                {/* Editor Header */}
                <div className="flex items-center justify-between p-4 border-b border-[#504A4A]">
                  <div>
                    <h2 className="text-lg font-semibold text-[#EBFFEE]">
                      {editingId ? 'Edit Waiver Content' : 'Create New Waiver'}
                    </h2>
                    {activeTemplate && (
                      <p className="text-xs text-[#A9A3A3] mt-1">
                        Current version: <span className="text-rollops-accent">v{activeTemplate.version}</span>
                        {hasChanges && <span className="text-yellow-400 ml-2">(unsaved changes)</span>}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded text-sm bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A] transition-colors"
                  >
                    {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {showPreview ? 'Edit' : 'Preview'}
                  </button>
                </div>

                {/* Placeholder Buttons */}
                {!showPreview && (
                  <div className="p-3 border-b border-[#504A4A] bg-[#1a1818]">
                    <p className="text-xs text-[#A9A3A3] mb-2">Insert placeholder (click to copy, then paste in editor):</p>
                    <div className="flex flex-wrap gap-1.5">
                      {WAIVER_PLACEHOLDERS.map(p => (
                        <button
                          key={p.key}
                          onClick={() => {
                            navigator.clipboard.writeText(`{{${p.key}}}`);
                            setNotification({ type: 'success', message: `Copied {{${p.key}}} to clipboard` });
                          }}
                          title={p.description}
                          className="px-2 py-1 text-xs rounded bg-[#363232] text-rollops-accent hover:bg-[#504A4A] border border-[#504A4A] transition-colors"
                        >
                          {`{{${p.key}}}`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Editor or Preview */}
                <div className="p-4">
                  {showPreview ? (
                    <div className="prose prose-invert max-w-none bg-[#363232] rounded-md p-4 border border-[#504A4A]">
                      <div
                        className="text-[#EBFFEE] [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:mt-6 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:mb-2 [&_p]:mb-3 [&_p]:leading-relaxed [&_strong]:font-bold [&_em]:italic [&_hr]:border-[#504A4A] [&_hr]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6"
                        dangerouslySetInnerHTML={{ __html: resolvePlaceholders(editorContent) }}
                      />
                    </div>
                  ) : (
                    <RichTextEditor
                      content={editorContent}
                      onChange={handleContentChange}
                      placeholder="Start writing your waiver content..."
                    />
                  )}
                </div>

                {/* Save Buttons */}
                <div className="flex flex-col sm:flex-row gap-2 p-4 border-t border-[#504A4A]">
                  {editingId && (
                    <Button
                      onClick={handleUpdateCurrent}
                      disabled={isSaving || !hasChanges}
                      className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-6 py-2 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isSaving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Update Current Version
                    </Button>
                  )}
                  <Button
                    onClick={handleSaveNewVersion}
                    disabled={isSaving || !editorContent.trim()}
                    className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] px-6 py-2 flex items-center justify-center gap-2 border border-[#504A4A] disabled:opacity-50"
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Save as v{nextVersion}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Version History */}
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#EBFFEE] mb-3">Version History</h3>

                {activeTemplate ? (
                  <div className="space-y-2">
                    {/* Active version */}
                    <button
                      onClick={() => handleLoadVersion(activeTemplate.id)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        editingId === activeTemplate.id
                          ? 'bg-[#9B1421]/20 border border-[#9B1421]'
                          : 'bg-[#363232] hover:bg-[#504A4A] border border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-[#EBFFEE]">v{activeTemplate.version}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-600 text-white">Active</span>
                      </div>
                      <p className="text-xs text-[#A9A3A3] mt-1">
                        {new Date(activeTemplate.created_at).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'short', day: 'numeric',
                        })}
                      </p>
                    </button>

                    {/* Older versions */}
                    {olderTemplates.length > 0 && (
                      <>
                        <button
                          onClick={() => setShowOlderVersions(!showOlderVersions)}
                          className="flex items-center gap-2 text-xs text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors w-full"
                        >
                          {showOlderVersions ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {olderTemplates.length} older version{olderTemplates.length !== 1 ? 's' : ''}
                        </button>
                        {showOlderVersions && (
                          <div className="space-y-1">
                            {olderTemplates.map(t => (
                              <button
                                key={t.id}
                                onClick={() => handleLoadVersion(t.id)}
                                className={`w-full text-left p-2 rounded transition-colors ${
                                  editingId === t.id
                                    ? 'bg-[#9B1421]/20 border border-[#9B1421]'
                                    : 'bg-[#363232] hover:bg-[#504A4A] border border-transparent'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-[#EBFFEE]">v{t.version}</span>
                                  {!t.is_active && (
                                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#504A4A] text-[#A9A3A3]">Inactive</span>
                                  )}
                                </div>
                                <p className="text-xs text-[#A9A3A3] mt-0.5">
                                  {new Date(t.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric', month: 'short', day: 'numeric',
                                  })}
                                </p>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-[#A9A3A3]">No versions yet. Save your first version to get started.</p>
                )}
              </CardContent>
            </Card>

            {/* Placeholder Reference */}
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#EBFFEE] mb-3">Placeholder Reference</h3>
                <div className="space-y-2">
                  {WAIVER_PLACEHOLDERS.map(p => (
                    <div key={p.key} className="text-xs">
                      <code className="text-rollops-accent">{`{{${p.key}}}`}</code>
                      <p className="text-[#A9A3A3] mt-0.5">{p.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
